import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { config } from "./config"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formats a date string into a human-readable format
 * @param dateString The date string to format
 * @param includeTime Whether to include the time in the formatted date
 * @returns Formatted date string
 */
export function formatDate(dateString: string, includeTime: boolean = false): string {
  const date = new Date(dateString);

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return 'Invalid date';
  }

  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  };

  if (includeTime) {
    options.hour = 'numeric';
    options.minute = 'numeric';
  }

  return date.toLocaleDateString('en-GB', options);
}

/**
 * Format a date relative to now in a human-readable format
 * @param date The date to format
 * @returns Formatted relative time string
 */
export function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  // Convert milliseconds to seconds
  const seconds = Math.floor(diff / 1000);

  if (seconds < 60) {
    return `just now`;
  }

  // Convert to minutes
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  }

  // Convert to hours
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  }

  // Convert to days
  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  }

  // For older dates, use the regular format
  return formatDate(date.toISOString());
}

/**
 * Encodes a string to Base64
 * @param str The string to encode
 * @returns Base64 encoded string
 */
export function encodeBase64(str: string): string {
  return btoa(str);
}

/**
 * Decodes a Base64 string
 * @param str The Base64 string to decode
 * @returns Decoded string
 */
export function decode(str: string): string {
  return atob(str);
}

/**
 * Debug utility for logging
 * Use this instead of direct console.log/error calls
 */
export const debug = {
  /**
   * Logs messages only in development environment
   * @param args Arguments to log
   */
  log: (...args: unknown[]): void => {
    if (config.features.enableDebugLogging) {
      // eslint-disable-next-line no-console, no-restricted-syntax
      console.log(...args);
    }
  },

  /**
   * Logs errors in all environments
   * @param args Arguments to log
   */
  error: (...args: unknown[]): void => {
    // eslint-disable-next-line no-console, no-restricted-syntax
    console.error(...args);
  },

  /**
   * Logs warnings only in development environment
   * @param args Arguments to log
   */
  warn: (...args: unknown[]): void => {
    if (config.features.enableDebugLogging) {
      // eslint-disable-next-line no-console, no-restricted-syntax
      console.warn(...args);
    }
  },

  /**
   * Logs info only in development environment
   * @param args Arguments to log
   */
  info: (...args: unknown[]): void => {
    if (config.features.enableDebugLogging) {
      // eslint-disable-next-line no-console, no-restricted-syntax
      console.info(...args);
    }
  }
}
